import { doc, getDoc, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { firestoreDb } from './firebase'

export interface PublicProfile {
  uid: string
  displayName?: string
  firstName?: string
  lastName?: string
  username?: string
  photoURL?: string
  profileImage?: {
    source?: string
    avatarId?: string
  }
  updatedAt?: unknown
}

const mapSnapshotToProfile = (id: string, data: Record<string, unknown> | undefined): PublicProfile => ({
  uid: id,
  displayName: typeof data?.displayName === 'string' ? data.displayName : '',
  firstName: typeof data?.firstName === 'string' ? data.firstName : '',
  lastName: typeof data?.lastName === 'string' ? data.lastName : '',
  username: typeof data?.username === 'string' ? data.username : '',
  photoURL: typeof data?.photoURL === 'string' ? data.photoURL : '',
  profileImage: data?.profileImage as PublicProfile['profileImage'],
  updatedAt: data?.updatedAt,
})

export const getPublicProfile = async (uid: string): Promise<PublicProfile | null> => {
  if (!uid) return null
  const snap = await getDoc(doc(firestoreDb, 'public_profiles', uid))
  if (!snap.exists()) return null
  return mapSnapshotToProfile(snap.id, snap.data())
}

export const getPublicProfiles = async (uids: string[]): Promise<Map<string, PublicProfile>> => {
  const result = new Map<string, PublicProfile>()
  const uniqueUids = Array.from(new Set(uids.filter(Boolean)))
  if (uniqueUids.length === 0) return result

  const profiles = await Promise.all(
    uniqueUids.map(async (uid) => {
      const profile = await getPublicProfile(uid)
      return { uid, profile }
    })
  )

  for (const { uid, profile } of profiles) {
    if (profile) {
      result.set(uid, profile)
    }
  }

  return result
}

export const subscribePublicProfile = (
  uid: string,
  callback: (profile: PublicProfile | null) => void,
): Unsubscribe => {
  if (!uid) {
    callback(null)
    return () => {}
  }

  return onSnapshot(
    doc(firestoreDb, 'public_profiles', uid),
    (snap) => {
      if (!snap.exists()) {
        callback(null)
        return
      }
      callback(mapSnapshotToProfile(snap.id, snap.data()))
    },
    (err) => {
      console.error(`Failed to subscribe to public_profile for ${uid}:`, err)
      callback(null)
    }
  )
}
